// F10.2 — STUN-based public IP detection.
//
// We need each peer's browser to learn its own public (srflx) IPv4 so the
// presence layer can write it into Firestore. Two peers in the same room
// whose `publicIp` matches are flagged `sameLAN: true` (they're behind the
// same NAT).
//
// How: spin up a throwaway RTCPeerConnection with STUN servers configured,
// open a no-op DataChannel to force ICE gathering, listen for
// `icecandidate`, and pull the address out of the first `typ srflx` IPv4
// candidate. 3-second timeout returns null.
//
// We deliberately accept IPv4 only. IPv6 srflx addresses are usually
// per-device unique even within the same network (no NAT in IPv6), so
// matching on IPv6 would produce false-negative `sameLAN`. The cost is
// that IPv6-only networks won't get the LAN badge — acceptable.

const STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

const TIMEOUT_MS = 3000;

// Module-level cache. Re-runs only on `online` event after a network change.
let cached = null;
let inflight = null;

/**
 * Reset the cached public IP. Call on `online` event so the next read picks
 * up the new network's NAT IP. We avoid `offline` because we don't want to
 * blank the cache while the user is mid-session — they may come back on the
 * same network.
 */
export function resetPublicIpCache() {
  cached = null;
  inflight = null;
}

// Auto-wire the `online` event once per module load. In test environments
// `window` may be missing — guard.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('online', resetPublicIpCache);
}

/**
 * Detect this browser's public IPv4 via STUN. Cached for the session.
 *
 * @returns {Promise<string | null>}
 */
export function detectPublicIp() {
  if (cached !== null) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = doDetect().then(
    (ip) => {
      cached = ip;
      inflight = null;
      return ip;
    },
    () => {
      // Errors are swallowed — STUN failure is "unknown public IP", not a
      // fatal condition. We just don't get to render the sameLAN badge.
      cached = null;
      inflight = null;
      return null;
    },
  );
  return inflight;
}

async function doDetect() {
  // Guard against environments without RTCPeerConnection (Node tests that
  // forget to stub it). Resolving to null is friendlier than throwing.
  const PC =
    (typeof globalThis !== 'undefined' && globalThis.RTCPeerConnection) ||
    (typeof window !== 'undefined' && window.RTCPeerConnection);
  if (!PC) return null;

  const pc = new PC({ iceServers: STUN_SERVERS });
  let resolved = false;
  let timer = null;

  const cleanup = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    try {
      pc.close();
    } catch {
      /* ignore */
    }
  };

  return new Promise((resolve) => {
    const finish = (ip) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(ip);
    };

    pc.addEventListener('icecandidate', (e) => {
      // null candidate = gathering complete. If we got here without a hit,
      // we never saw an IPv4 srflx — give up with null.
      if (!e.candidate) {
        if (!resolved) finish(null);
        return;
      }
      const ip = extractSrflxIPv4(e.candidate.candidate);
      if (ip) finish(ip);
    });

    // Open a no-op DataChannel to force ICE gathering. Without this, some
    // browsers won't gather any candidates because there's nothing to send.
    try {
      pc.createDataChannel('beam-probe');
    } catch {
      // Some test mocks don't implement createDataChannel; gathering will
      // either still happen or it won't, and we'll time out.
    }

    // createOffer() + setLocalDescription() is what actually kicks off
    // gathering on standards-compliant implementations.
    Promise.resolve()
      .then(() => pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false }))
      .then((offer) => pc.setLocalDescription(offer))
      .catch(() => {
        // Mock-friendly: createOffer may not exist in unit tests. The
        // 'icecandidate' listener still works because tests dispatch the
        // event manually.
      });

    timer = setTimeout(() => finish(null), TIMEOUT_MS);
  });
}

// Candidate line format (RFC 5245 §15.1):
//   "candidate:<foundation> <component> <transport> <priority> <address>
//    <port> typ <type> [raddr <ip> rport <port>] ..."
// We want the <address> immediately following the priority, but only when
// the trailing `typ <type>` is `srflx`. The address must be IPv4 dotted-quad.
const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function extractSrflxIPv4(candidateLine) {
  if (typeof candidateLine !== 'string') return null;
  // Cheap pre-filter — bail before regex if the line isn't srflx.
  if (!candidateLine.includes(' typ srflx')) return null;
  const parts = candidateLine.split(/\s+/);
  // SDP layout: ["candidate:foundation", component, transport, priority,
  //              address, port, "typ", type, ...]
  if (parts.length < 8) return null;
  if (parts[6] !== 'typ' || parts[7] !== 'srflx') return null;
  const addr = parts[4];
  if (!IPV4_RE.test(addr)) return null;
  // Sanity-check each octet is 0–255.
  const octets = addr.split('.').map((s) => Number(s));
  if (octets.some((n) => n < 0 || n > 255)) return null;
  return addr;
}

export const __test__ = { extractSrflxIPv4, TIMEOUT_MS };
